/** @jsx jsx */
import type { SerializedStyles } from '@emotion/react';
import { jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { VideoIcon } from '@coursera/cds-icons';

type Props = {
  videoUrl: string;
  timestampSeconds: number;
  cssOverride?: () => SerializedStyles;
};

const VideoPreviewAtTimestamp = ({ videoUrl, timestampSeconds, cssOverride }: Props) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [errorLoading, setErrorLoading] = useState(false);

  useEffect(() => {
    const video = videoRef.current;

    if (!video) {
      return undefined;
    }

    const handleLoadedMetadata = () => {
      video.currentTime = timestampSeconds;
    };

    // capture error if there's an issue with setting timestamp for metadata
    const handleError = () => {
      setErrorLoading(true);
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('error', handleError);

    // load the video metadata to trigger the events
    video.load();

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('error', handleError);
    };
  }, [videoUrl, timestampSeconds]);

  return (
    <div css={cssOverride} data-testid="video-preview-timestamp">
      {errorLoading ? (
        <VideoIcon />
      ) : (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video
          ref={videoRef}
          preload="metadata"
          controls={false}
          width="100%"
          height="100%"
          onError={() => setErrorLoading(true)}
        >
          <source src={videoUrl} type="video/webm" />
        </video>
      )}
    </div>
  );
};

export default VideoPreviewAtTimestamp;
